import React, { FC, Fragment, ReactNode, useState } from 'react';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { connect, FormikProps } from 'formik';
import { Button, Collapse, Divider } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';

import { Content, ContentSharePreview, ContentUrlParamOptions } from 'api';
import { Queries } from 'components';

import { Commentary, URLTracking } from './';

type QueryResults = (
  sharePreview: ContentSharePreview,
  urlParams: ContentUrlParamOptions[],
) => ReactNode;

interface IProps {
  allowUtm: boolean;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<Content>;
}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    divider: {
      margin: `${theme.spacing(3)}px -${theme.spacing(2)}px 0`,
    },
  });

const COLLAPSED_HEIGHT = 52;

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

const AdminCustomization: FC<IPropsEnhanced> = ({ classes, formik, allowUtm }) => {
  const [expanded, setExpanded] = useState(false);

  const { t } = useTranslation();
  const { values } = formik;

  function handleToggle() {
    setExpanded(!expanded);
  }

  function renderButton(urlParams: ContentUrlParamOptions[]) {
    const showUtm = allowUtm && !!urlParams.length;
    const i18nKey = t(!!showUtm ? 'posting:editShareCopyAndUTM' : 'posting:editShareCopy');
    const icon = expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />;

    return (
      <Button
        fullWidth
        type="button"
        size="medium"
        variant="text"
        className={classes.button}
        endIcon={icon}
        onClick={handleToggle}>
        {t(i18nKey)}
      </Button>
    );
  }

  const renderQueryResults: QueryResults = (sharePreview, urlParams) => {
    const { allowed_counts = {} } = sharePreview || {};
    const showUtm = allowUtm && !!urlParams.length;

    return (
      <Collapse in={expanded} collapsedHeight={`${COLLAPSED_HEIGHT}px`}>
        {expanded && (
          <Fragment>
            <Commentary allowedCounts={allowed_counts} />
            <Divider className={classes.divider} />
            {!!showUtm && <URLTracking contentUrlParamOptions={urlParams} />}
          </Fragment>
        )}
        {renderButton(urlParams)}
      </Collapse>
    );
  };

  return (
    <Queries.GetSharePreviewInfo loader={renderButton([])} content_id={values.content_id}>
      {sharePreviewInfo => (
        <Queries.GetContentUrlParamOptions
          loader={renderButton([])}
          url={values.content_object.url}>
          {contentUrlParamOptions => renderQueryResults(sharePreviewInfo, contentUrlParamOptions)}
        </Queries.GetContentUrlParamOptions>
      )}
    </Queries.GetSharePreviewInfo>
  );
};

export default enhancers(AdminCustomization);
